/**
 * Venues Store - Venue listing state management
 * 
 * Manages venue data, filtering, sorting, and view state
 * for the venue explorer page.
 */
import { create } from 'zustand';

// Mock venue data
const MOCK_VENUES = [
  {
    id: 1,
    name: 'Metro Sports Center',
    sports: ['basketball', 'volleyball', 'badminton'],
    rating: 4.8,
    reviewCount: 124,
    distance: 0.8,
    address: '456 Metro Ave, New York, NY',
    amenities: ['Parking', 'Showers', 'Lockers', 'Equipment Rental'],
    isOpen: true,
    hours: '6:00 AM - 10:00 PM',
    coordinates: { lat: 40.7589, lng: -73.9851 },
  },
  {
    id: 2,
    name: 'Sunset Basketball Courts',
    sports: ['basketball'],
    rating: 4.5,
    reviewCount: 89,
    distance: 1.2,
    address: '789 Sunset Blvd, New York, NY',
    amenities: ['Lights', 'Benches', 'Water Fountain'],
    isOpen: true,
    hours: 'Open 24 hours',
    coordinates: { lat: 40.7505, lng: -73.9934 },
  },
  {
    id: 3,
    name: 'Aquatic Fitness Pool',
    sports: ['swimming'],
    rating: 4.7,
    reviewCount: 203,
    distance: 2.1,
    address: '321 Pool St, New York, NY',
    amenities: ['Parking', 'Showers', 'Lockers', 'Sauna'],
    isOpen: false,
    hours: '5:00 AM - 9:00 PM',
    coordinates: { lat: 40.7614, lng: -73.9776 },
  },
  {
    id: 4,
    name: 'Central Park Tennis Courts',
    sports: ['tennis'],
    rating: 4.6,
    reviewCount: 156,
    distance: 0.5,
    address: 'Central Park West, New York, NY',
    amenities: ['Lights', 'Restrooms', 'Benches'],
    isOpen: true,
    hours: '6:00 AM - 11:00 PM',
    coordinates: { lat: 40.7829, lng: -73.9654 },
  },
  {
    id: 5,
    name: 'Brooklyn Soccer Complex',
    sports: ['soccer', 'football'],
    rating: 4.4,
    reviewCount: 78,
    distance: 3.2,
    address: '789 Field Ave, Brooklyn, NY',
    amenities: ['Parking', 'Restrooms', 'Concessions'],
    isOpen: true,
    hours: '7:00 AM - 9:00 PM',
    coordinates: { lat: 40.6892, lng: -73.9442 },
  },
  {
    id: 6,
    name: 'Queens Rock Climbing Gym',
    sports: ['rock climbing'],
    rating: 4.9,
    reviewCount: 312,
    distance: 4.5,
    address: '123 Climb St, Queens, NY',
    amenities: ['Parking', 'Showers', 'Lockers', 'Gear Shop'],
    isOpen: true,
    hours: '10:00 AM - 11:00 PM',
    coordinates: { lat: 40.7282, lng: -73.7949 },
  },
];

// Sport icons mapping
const SPORT_ICONS = {
  basketball: '🏀',
  volleyball: '🏐',
  badminton: '🏸',
  swimming: '🏊',
  tennis: '🎾',
  soccer: '⚽',
  football: '🏈',
  'rock climbing': '🧗',
};

// Amenity icons mapping
const AMENITY_ICONS = {
  'Parking': 'P',
  'Showers': '🚿',
  'Lockers': '🔒',
  'Equipment Rental': '🎒',
  'Lights': '💡',
  'Benches': '🪑',
  'Water Fountain': '💧',
  'Sauna': '🔥',
  'Restrooms': '🚻',
  'Concessions': '🍔',
  'Gear Shop': '🛍️',
};

export const useVenuesStore = create((set, get) => ({
  // Data
  venues: MOCK_VENUES,
  
  // View state
  viewMode: 'list', // 'list' | 'map'
  selectedVenueId: null,
  
  // Filter state
  filters: {
    sportTypes: [],
    amenities: [],
    maxDistance: 10,
    openNow: false,
    minRating: 0,
  },
  
  // Sort state
  sortBy: 'distance', // 'distance' | 'rating' | 'name'
  
  // Search
  searchQuery: '',
  
  // Filter panel visibility
  isFilterPanelOpen: false,
  
  // Actions
  setViewMode: (mode) => set({ viewMode: mode }),
  
  selectVenue: (venueId) => set({ selectedVenueId: venueId }),
  
  clearSelection: () => set({ selectedVenueId: null }),
  
  setSortBy: (sortBy) => set({ sortBy }),
  
  setSearchQuery: (query) => set({ searchQuery: query }),
  
  toggleFilterPanel: () => set((state) => ({ 
    isFilterPanelOpen: !state.isFilterPanelOpen 
  })),
  
  closeFilterPanel: () => set({ isFilterPanelOpen: false }),
  
  // Filter actions
  toggleSportType: (sport) => set((state) => {
    const currentSports = state.filters.sportTypes;
    const newSports = currentSports.includes(sport)
      ? currentSports.filter(s => s !== sport)
      : [...currentSports, sport];
    return {
      filters: { ...state.filters, sportTypes: newSports }
    };
  }),
  
  toggleAmenity: (amenity) => set((state) => {
    const currentAmenities = state.filters.amenities;
    const newAmenities = currentAmenities.includes(amenity)
      ? currentAmenities.filter(a => a !== amenity)
      : [...currentAmenities, amenity];
    return {
      filters: { ...state.filters, amenities: newAmenities }
    };
  }),
  
  setMaxDistance: (distance) => set((state) => ({
    filters: { ...state.filters, maxDistance: distance }
  })),
  
  toggleOpenNow: () => set((state) => ({
    filters: { ...state.filters, openNow: !state.filters.openNow }
  })),
  
  setMinRating: (rating) => set((state) => ({
    filters: { ...state.filters, minRating: rating }
  })),
  
  clearFilters: () => set({
    filters: {
      sportTypes: [],
      amenities: [],
      maxDistance: 10,
      openNow: false,
      minRating: 0,
    }
  }),
  
  // Computed - Get filtered and sorted venues
  getFilteredVenues: () => {
    const { venues, filters, sortBy, searchQuery } = get();
    
    let result = venues.filter(venue => {
      // Search filter
      if (searchQuery && !venue.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      
      // Sport type filter
      if (filters.sportTypes.length > 0 && 
          !venue.sports.some(sport => filters.sportTypes.includes(sport))) {
        return false;
      }
      
      // Amenity filter
      if (filters.amenities.length > 0 && 
          !filters.amenities.every(amenity => venue.amenities.includes(amenity))) {
        return false;
      }
      
      // Distance filter
      if (venue.distance > filters.maxDistance) {
        return false;
      }
      
      // Open now filter
      if (filters.openNow && !venue.isOpen) {
        return false;
      }
      
      // Rating filter
      if (venue.rating < filters.minRating) {
        return false;
      }
      
      return true;
    });
    
    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'distance':
          return a.distance - b.distance;
        case 'rating':
          return b.rating - a.rating;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });
    
    return result;
  },
  
  // Get available filters from data
  getAvailableSports: () => {
    const { venues } = get();
    const sports = new Set();
    venues.forEach(venue => {
      venue.sports.forEach(sport => sports.add(sport));
    });
    return Array.from(sports).sort();
  },
  
  getAvailableAmenities: () => {
    const { venues } = get();
    const amenities = new Set();
    venues.forEach(venue => {
      venue.amenities.forEach(amenity => amenities.add(amenity));
    });
    return Array.from(amenities).sort();
  },
  
  // Get selected venue
  getSelectedVenue: () => {
    const { venues, selectedVenueId } = get();
    return venues.find(v => v.id === selectedVenueId) || null;
  },
  
  // Helpers
  getSportIcon: (sport) => SPORT_ICONS[sport] || '⚽',
  getAmenityIcon: (amenity) => AMENITY_ICONS[amenity] || '✓',
}));

export default useVenuesStore;
